import { Pause, Play, Volume2, VolumeX } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { SegmentResponse, WordDetail, WordResponse } from '@/api/type'
import { RangeSlider } from '@/components/ui/slider'
import { speechAligner } from '@/lib/speech-aligner'

import { useWebAudio } from '../../hooks/useWebAudio'
import { ControlButton } from '../playback-controls/control-button'

const PLAYBACK_MODES = {
    CASSETTE: 'cassette',
    OVERLAP: 'overlap',
    MANUAL: 'manual',
} as const

export type PlaybackMode = (typeof PLAYBACK_MODES)[keyof typeof PLAYBACK_MODES]

type PronunciationComparisonProps = {
    wordData?: WordDetail
    userAudioBlob: Blob
    referenceAudioUrl: string
    referenceWordTiming?: WordResponse
    // 1文全体の再生用プロパティ
    fullSentence?: boolean
    words?: WordDetail[] // 1文全体の場合に使用
    // セグメント単位の再生用プロパティ
    segmentMode?: boolean
    segment?: SegmentResponse
}

export const PronunciationComparison = ({
    wordData,
    userAudioBlob,
    referenceAudioUrl,
    referenceWordTiming,
    fullSentence = false,
    words = [],
    segmentMode = false,
    segment,
}: PronunciationComparisonProps) => {
    const [mode, setMode] = useState<PlaybackMode>(() => {
        const saved = localStorage.getItem('pronunciation_comparison_mode')
        return (saved as PlaybackMode) || PLAYBACK_MODES.CASSETTE
    })
    const [isPlaying, setIsPlaying] = useState(false)
    const [userVolume, setUserVolume] = useState(50)
    const [referenceVolume, setReferenceVolume] = useState(50)

    const [userAudioUrl, setUserAudioUrl] = useState<string>('')

    // モード変更をLocalStorageに保存
    const handleModeChange = (newMode: PlaybackMode) => {
        setMode(newMode)
        localStorage.setItem('pronunciation_comparison_mode', newMode)
    }

    // ユーザー音声のBlobからURLを作成
    useEffect(() => {
        const url = URL.createObjectURL(userAudioBlob)
        setUserAudioUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [userAudioBlob])

    // Web Audio APIを使用（iPhoneでのシーク遅延を解消）
    const userAudio = useWebAudio(userAudioUrl)
    const referenceAudio = useWebAudio(referenceAudioUrl)

    // 音量を設定（Web Audio APIでは音量制御が難しいため、HTML5 audioと併用）
    // TODO: Web Audio APIのGainNodeで音量制御を実装
    // 現在は簡易的にHTML5 audioを使用

    // セグメント内の単語アライメントをメモ化
    const segmentAlignment = useMemo(() => {
        if (!segmentMode || !segment || !segment.words || segment.words.length === 0) {
            return null
        }
        // Needleman-Wunschアルゴリズムでアライメント
        return speechAligner.align(segment.words, words)
    }, [segmentMode, segment, words])

    // ワードのオフセットとデュレーションを使用して音声を再生(単語単位)
    const playUserAudio = () => {
        if (segmentMode && segment && segmentAlignment) {
            // Needleman-Wunschアルゴリズムでアライメントされた結果から最初と最後のWordDetailを取得
            const alignedWords = segmentAlignment
                .filter(item => item.type === 'MATCH' || item.type === 'SUBSTITUTION')
                .map(item => item.hyp)
                .filter((w): w is WordDetail => w !== null)

            if (alignedWords.length > 0) {
                const firstWord = alignedWords[0]
                const lastWord = alignedWords[alignedWords.length - 1]
                const offsetSeconds = firstWord.Offset / 10_000_000
                const endSeconds = (lastWord.Offset + lastWord.Duration) / 10_000_000

                userAudio.play(offsetSeconds)

                // 最後の単語の終了時に停止
                const durationSeconds = endSeconds - offsetSeconds
                setTimeout(() => {
                    userAudio.pause()
                }, durationSeconds * 1000)
            } else {
                // アライメントで一致する単語が見つからない場合
                console.warn('No aligned WordDetail found for segment')
            }
        } else if (fullSentence && words.length > 0) {
            // 1文全体を再生（最初の単語の開始位置から）
            const firstWord = words[0]
            const offsetSeconds = firstWord.Offset / 10_000_000
            userAudio.play(offsetSeconds)
        } else if (wordData) {
            // 単語のみを再生
            const offsetSeconds = wordData.Offset / 10_000_000 // 100ns単位から秒へ変換
            const durationSeconds = wordData.Duration / 10_000_000

            userAudio.play(offsetSeconds)

            // 指定された時間後に停止
            setTimeout(() => {
                userAudio.pause()
            }, durationSeconds * 1000)
        }
    }

    const playReferenceAudio = () => {
        if (segmentMode && segment) {
            // セグメント単位で再生
            referenceAudio.play(segment.start)

            // セグメント終了時に停止
            const durationSeconds = segment.end - segment.start
            setTimeout(() => {
                referenceAudio.pause()
            }, durationSeconds * 1000)
        } else if (fullSentence) {
            // 1文全体を再生（referenceWordsがあれば最初の単語の開始位置から、なければ先頭から）
            const offsetSeconds = referenceWordTiming ? referenceWordTiming.start : 0
            referenceAudio.play(offsetSeconds)
        } else {
            // 単語のみを再生
            // お手本音声にはWordResponseのstart/endを使用
            const offsetSeconds = referenceWordTiming
                ? referenceWordTiming.start
                : wordData
                    ? wordData.Offset / 10_000_000
                    : 0
            const durationSeconds = referenceWordTiming
                ? (referenceWordTiming.end - referenceWordTiming.start)
                : wordData
                    ? wordData.Duration / 10_000_000
                    : 0

            referenceAudio.play(offsetSeconds)

            setTimeout(() => {
                referenceAudio.pause()
            }, durationSeconds * 1000)
        }
    }

    // 1文全体または単語の長さを取得
    const getUserDuration = () => {
        if (segmentMode && segment && segmentAlignment) {
            // Needleman-Wunschアルゴリズムでアライメントされた結果から長さを計算
            const alignedWords = segmentAlignment
                .filter(item => item.type === 'MATCH' || item.type === 'SUBSTITUTION')
                .map(item => item.hyp)
                .filter((w): w is WordDetail => w !== null)

            if (alignedWords.length > 0) {
                // 最初の単語の開始から最後の単語の終了までの時間
                const firstWord = alignedWords[0]
                const lastWord = alignedWords[alignedWords.length - 1]
                const startSeconds = firstWord.Offset / 10_000_000
                const endSeconds = (lastWord.Offset + lastWord.Duration) / 10_000_000
                return endSeconds - startSeconds
            }
            // アライメントで一致する単語が見つからない場合はセグメントの長さを返す
            return segment.end - segment.start
        }
        if (fullSentence && words.length > 0) {
            // 全ての単語の最後までの長さを計算
            const lastWord = words[words.length - 1]
            return (lastWord.Offset + lastWord.Duration) / 10_000_000
        }
        return wordData ? wordData.Duration / 10_000_000 : 0
    }

    const getReferenceDuration = () => {
        if (segmentMode && segment) {
            return segment.end - segment.start
        }
        if (fullSentence) {
            // 音声の全体の長さを使用
            return referenceAudio.duration || 0
        }
        return referenceWordTiming
            ? (referenceWordTiming.end - referenceWordTiming.start)
            : wordData
                ? wordData.Duration / 10_000_000
                : 0
    }

    // カセットテープ方式: お手本 → ユーザー
    const playCassetteMode = async () => {
        setIsPlaying(true)
        playReferenceAudio()

        // お手本の再生が終わったらユーザー音声を再生
        const referenceDuration = getReferenceDuration()
        const userDuration = getUserDuration()

        setTimeout(() => {
            playUserAudio()
            setTimeout(() => {
                setIsPlaying(false)
            }, userDuration * 1000)
        }, referenceDuration * 1000 + 300) // 300ms の間を空ける
    }

    // オーバーラップ方式: 同時再生
    const playOverlapMode = () => {
        setIsPlaying(true)
        playUserAudio()
        playReferenceAudio()

        const referenceDuration = getReferenceDuration()
        const userDuration = getUserDuration()
        const maxDuration = Math.max(referenceDuration, userDuration)

        setTimeout(() => {
            setIsPlaying(false)
        }, maxDuration * 1000)
    }

    // 停止
    const stopAllAudio = () => {
        userAudio.pause()
        referenceAudio.pause()
        setIsPlaying(false)
    }

    // 個別操作用の状態
    const [isUserPlaying, setIsUserPlaying] = useState(false)
    const [isReferencePlaying, setIsReferencePlaying] = useState(false)

    const toggleUserAudio = () => {
        if (isUserPlaying) {
            userAudio.pause()
            setIsUserPlaying(false)
        } else {
            playUserAudio()
            setIsUserPlaying(true)
            const durationSeconds = getUserDuration()
            setTimeout(() => {
                setIsUserPlaying(false)
            }, durationSeconds * 1000)
        }
    }

    const toggleReferenceAudio = () => {
        if (isReferencePlaying) {
            referenceAudio.pause()
            setIsReferencePlaying(false)
        } else {
            playReferenceAudio()
            setIsReferencePlaying(true)
            const durationSeconds = getReferenceDuration()
            setTimeout(() => {
                setIsReferencePlaying(false)
            }, durationSeconds * 1000)
        }
    }

    return (
        <div className="mt-4 bg-linear-to-br from-indigo-950 via-purple-950 to-indigo-950 text-white rounded-xl p-4 shadow-2xl border border-indigo-700">
            {/* Web Audio APIを使用（audio要素は不要） */}

            <div className="mb-4">
                <h3 className="text-sm font-bold text-indigo-300 mb-3 flex items-center gap-2">
                    <Volume2 className="w-4 h-4" />
                    {segmentMode ? 'セグメント単位の発音比較' : fullSentence ? '1文全体の発音比較' : '単語の発音比較'}
                </h3>

                {/* モード選択 */}
                <div className="flex justify-center gap-2 mb-4">
                    <div className={`transition-opacity ${mode === PLAYBACK_MODES.CASSETTE ? 'opacity-100' : 'opacity-50'}`}>
                        <ControlButton
                            onClick={() => handleModeChange(PLAYBACK_MODES.CASSETTE)}
                            icon={<span className="text-2xl">📼</span>}
                            label={PLAYBACK_MODES.CASSETTE}
                        />
                    </div>
                    <div className={`transition-opacity ${mode === PLAYBACK_MODES.OVERLAP ? 'opacity-100' : 'opacity-50'}`}>
                        <ControlButton
                            onClick={() => handleModeChange(PLAYBACK_MODES.OVERLAP)}
                            icon={<span className="text-2xl">🎵</span>}
                            label={PLAYBACK_MODES.OVERLAP}
                        />
                    </div>
                    <div className={`transition-opacity ${mode === PLAYBACK_MODES.MANUAL ? 'opacity-100' : 'opacity-50'}`}>
                        <ControlButton
                            onClick={() => handleModeChange(PLAYBACK_MODES.MANUAL)}
                            icon={<span className="text-2xl">🎚️</span>}
                            label={PLAYBACK_MODES.MANUAL}
                        />
                    </div>
                </div>
            </div>

            {/* カセットテープモード */}
            {mode === PLAYBACK_MODES.CASSETTE && (
                <div className="space-y-3">
                    <p className="text-xs text-indigo-200 mb-2">
                        {segmentMode
                            ? 'セグメントのお手本を聞いた後、自分の発音を自動再生します。'
                            : fullSentence
                                ? 'お手本の1文全体を聞いた後、自分の発音を自動再生します。'
                                : 'お手本を聞いた後、自分の発音を自動再生します。発音の細かい違いを確認できます。'}
                    </p>
                    <button
                        onClick={isPlaying ? stopAllAudio : playCassetteMode}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                        {isPlaying ? '停止' : '比較再生'}
                    </button>
                </div>
            )}

            {/* オーバーラップモード */}
            {mode === PLAYBACK_MODES.OVERLAP && (
                <div className="space-y-3">
                    <p className="text-xs text-purple-200 mb-2">
                        {segmentMode
                            ? 'セグメントのお手本と自分の声を同時に流します。リズム・イントネーション・スピードのズレを確認できます。'
                            : fullSentence
                                ? 'お手本と自分の声を同時に流します。1文全体のリズム・イントネーション・スピードのズレを確認できます。'
                                : 'お手本と自分の声を同時に流します。リズム・イントネーション・スピードのズレを確認できます。'}
                    </p>

                    {/* 音量ミキサー */}
                    <div className="space-y-3 bg-purple-900/20 p-3 rounded-lg border border-purple-800/50">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-purple-200">お手本の音量</span>
                                <span className="text-xs font-bold text-purple-300">{referenceVolume}%</span>
                            </div>
                            <RangeSlider
                                value={[referenceVolume]}
                                onValueChange={(values: number[]) => setReferenceVolume(values[0])}
                                max={100}
                                step={1}
                                className="w-full"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-purple-200">自分の音量</span>
                                <span className="text-xs font-bold text-purple-300">{userVolume}%</span>
                            </div>
                            <RangeSlider
                                value={[userVolume]}
                                onValueChange={(values: number[]) => setUserVolume(values[0])}
                                max={100}
                                step={1}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <button
                        onClick={isPlaying ? stopAllAudio : playOverlapMode}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                        {isPlaying ? '停止' : '同時再生'}
                    </button>
                </div>
            )}

            {/* 個別操作モード */}
            {mode === PLAYBACK_MODES.MANUAL && (
                <div className="space-y-3">
                    <p className="text-xs text-pink-200 mb-2">
                        {segmentMode
                            ? 'セグメントのお手本と自分の音声をそれぞれ独立して再生できます。'
                            : fullSentence
                                ? 'お手本と自分の音声をそれぞれ独立して再生できます。'
                                : 'それぞれ独立して再生できます。納得いくまで聞き直せます。'}
                    </p>

                    {/* お手本ボタン */}
                    <button
                        onClick={toggleReferenceAudio}
                        className="w-full bg-pink-600 hover:bg-pink-700 text-white px-4 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                        {isReferencePlaying ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        お手本の発音 {isReferencePlaying ? '(再生中)' : ''}
                    </button>

                    {/* 自分の発音ボタン */}
                    <button
                        onClick={toggleUserAudio}
                        className="w-full bg-pink-700 hover:bg-pink-800 text-white px-4 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                    >
                        {isUserPlaying ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        自分の発音 {isUserPlaying ? '(再生中)' : ''}
                    </button>
                </div>
            )}
        </div>
    )
}
